import React, { useState, useEffect } from 'react';
import { UserCog, Plus, Edit, Trash2, ToggleLeft, ToggleRight, Users, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ManagerAgents = ({ manager }) => {
  const { toast } = useToast();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/sales-agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedAgent) {
        await axios.put(`${BACKEND_URL}/api/admin/sales-agents/${selectedAgent.agentId}`, formData);
        toast({ title: "Agent updated successfully" });
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/sales-agents`, formData);
        toast({ title: "Agent created successfully" });
      }
      setShowModal(false);
      setSelectedAgent(null);
      setFormData({ name: '', email: '', phone: '', password: '' });
      fetchAgents();
    } catch (error) {
      toast({ variant: "destructive", title: error.response?.data?.detail || "Operation failed" });
    }
  };

  const handleToggleActive = async (agent) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/sales-agents/${agent.agentId}`, {
        isActive: !agent.isActive
      });
      toast({ title: `Agent ${!agent.isActive ? 'activated' : 'deactivated'}` });
      fetchAgents();
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to update agent" });
    }
  };

  const handleDelete = async (agent) => {
    if (!window.confirm(`Delete agent ${agent.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/sales-agents/${agent.agentId}`);
      toast({ title: "Agent deleted successfully" });
      fetchAgents();
    } catch (error) {
      toast({ variant: "destructive", title: error.response?.data?.detail || "Failed to delete" });
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      email: agent.email,
      phone: agent.phone,
      password: ''
    });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Sales Agents Management</h2>
          <p className="text-slate-600">{agents.length} total agents</p>
        </div>
        <Button onClick={() => { setSelectedAgent(null); setShowModal(true); }} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Agent
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <div key={agent.agentId} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <UserCog className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{agent.name}</h3>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    agent.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4 text-sm text-slate-600">
              <p className="truncate">{agent.email}</p>
              <p>{agent.phone || 'N/A'}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-slate-200">
              <div>
                <p className="text-xs text-slate-500">Clients</p>
                <p className="font-semibold text-slate-800">{agent.totalClients || 0}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Revenue</p>
                <p className="font-semibold text-slate-800">₹{(agent.totalRevenue || 0).toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button size="sm" variant="outline" onClick={() => openEditModal(agent)} className="flex-1">
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleToggleActive(agent)}>
                {agent.isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleDelete(agent)} className="text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">{selectedAgent ? 'Edit' : 'Create'} Sales Agent</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
              />
              <input
                type="password"
                placeholder={selectedAgent ? "Password (leave empty to keep)" : "Password"}
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg"
                required={!selectedAgent}
              />
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                  {selectedAgent ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerAgents;